import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { ChartNoAxesCombined, Users, Settings, Activity, PesoSign } from '../components/Icons';

export interface AdminDashboardStats {
  totalUsers: number;
  activeUsers: number;
  newSignups: number;
  subscribers: number;
  mrr: number;
  churnRate: number;
  totalAssessments: number;
  totalConsultations: number;
}

interface RecentUserRow {
  id: string;
  name: string;
  email: string;
  status: string;
  plan: string;
  lastLogin: string | null;
}

export interface GrowthPoint {
  date: string;
  dayLabel: string;
  signups: number;
  activeUsers: number;
}

interface AlertItem {
  id: string;
  type: string;
  message: string;
}

function formatMRR(value: number): string {
  if (value === 0) return '₱0';
  if (value >= 1000) return `₱${(value / 1000).toFixed(1)}k`;
  return `₱${Math.round(value)}`;
}

function formatRelativeTime(isoDate: string | null): string {
  if (!isoDate) return '—';
  const d = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return d.toLocaleDateString();
}

const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [recentUsers, setRecentUsers] = useState<RecentUserRow[]>([]);
  const [recentUsersLoading, setRecentUsersLoading] = useState(true);
  const [growthTrend, setGrowthTrend] = useState<GrowthPoint[]>([]);
  const [growthTrendLoading, setGrowthTrendLoading] = useState(true);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState<'online' | 'offline' | null>(null);
  const [apiLatencyMs, setApiLatencyMs] = useState<number | null>(null);

  const getDisplayName = () => {
    if (!user) return '';
    if (user.profile?.preferredName) return user.profile.preferredName;
    if (user.profile?.firstName) return user.profile.firstName;
    if (user.email) return user.email;
    return '';
  };

  const fetchDashboardStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setStatsError('Not authenticated');
        return;
      }
      const res = await fetch(API_CONFIG.ENDPOINTS.ADMIN_DASHBOARD_STATS, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = await res.json();
      if (json.success && json.data) setStats(json.data);
      else setStatsError('Invalid response');
    } catch (e) {
      setStatsError(e instanceof Error ? e.message : 'Failed to load metrics');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchGrowthTrend = useCallback(async () => {
    try {
      setGrowthTrendLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_CONFIG.ENDPOINTS.ADMIN_GROWTH_TREND}?days=7`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json.success && Array.isArray(json.data?.series)) setGrowthTrend(json.data.series);
      else setGrowthTrend([]);
    } catch {
      setGrowthTrend([]);
    } finally {
      setGrowthTrendLoading(false);
    }
  }, []);

  const fetchHealth = useCallback(async () => {
    const t0 = performance.now();
    try {
      const res = await fetch(API_CONFIG.ENDPOINTS.API_HEALTH, { cache: 'no-store' });
      const latency = Math.round(performance.now() - t0);
      if (res.ok) {
        setApiStatus('online');
        setApiLatencyMs(latency);
      } else {
        setApiStatus('offline');
        setApiLatencyMs(null);
      }
    } catch {
      setApiStatus('offline');
      setApiLatencyMs(null);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      setAlertsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_CONFIG.ENDPOINTS.AUTH_ADMIN_REGISTRATION_STATS}?timeframe=24h`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) return;
      const json = await res.json();
      const list: AlertItem[] = [];
      const devicesInCooldown = json.data?.devicesInCooldown ?? 0;
      const flaggedIPs = json.data?.flaggedIPs ?? [];
      if (devicesInCooldown > 0) {
        list.push({ id: 'cooldown', type: 'security', message: `${devicesInCooldown} device(s) in registration cooldown.` });
      }
      if (flaggedIPs.length > 0) {
        list.push({ id: 'flagged', type: 'security', message: `${flaggedIPs.length} IP(s) flagged for review (last 24h).` });
      }
      setAlerts(list);
    } catch {
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  const fetchRecentUsers = useCallback(async () => {
    try {
      setRecentUsersLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      const params = new URLSearchParams({ limit: '5', page: '1' });
      const res = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_ALL}?${params}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        cache: 'no-store'
      });
      if (!res.ok) return;
      const json = await res.json();
      const users = (json.data?.users || []).map((u: {
        userId: string;
        name: string;
        email: string;
        status: string;
        plan: string;
        lastLogin: string | null;
      }) => ({
        id: u.userId,
        name: u.name || '—',
        email: u.email || '—',
        status: u.status === 'active' ? 'Active' : u.status === 'free' ? 'Free' : u.status || '—',
        plan: u.plan || 'Free',
        lastLogin: u.lastLogin || null
      }));
      setRecentUsers(users);
    } catch {
      setRecentUsers([]);
    } finally {
      setRecentUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentUsers();
    fetchGrowthTrend();
    fetchAlerts();
    fetchHealth();
    const healthInterval = setInterval(fetchHealth, 60 * 1000);
    return () => clearInterval(healthInterval);
  }, [fetchDashboardStats, fetchRecentUsers, fetchGrowthTrend, fetchAlerts, fetchHealth]);

  const renderKpiValue = (value: number | undefined, formatter?: (n: number) => string) => {
    if (statsLoading) return '—';
    if (statsError || value === undefined) return '—';
    return formatter ? formatter(value) : value.toLocaleString();
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard__container">
        {/* Main Title/Header */}
        <div className="admin-dashboard__header">
          <h1 className="admin-dashboard__title">Welcome, Admin{getDisplayName() ? ` ${getDisplayName()}` : ''}</h1>
        </div>
        <div className="admin-dashboard__main">
          <div className="admin-dashboard__content">
            {/* Key Metrics Section */}
            <section className="admin-dashboard__section">
              <div className="admin-dashboard__section-header admin-dashboard__section-header--flexgap">
                <ChartNoAxesCombined className="admin-dashboard__metrics-icon" />
                <h2 className="admin-dashboard__section-title">Key Metrics</h2>
              </div>
              {statsError && <p className="admin-dashboard__kpi-error">{statsError}</p>}
              <div className="admin-dashboard__kpis">
                <div className="admin-dashboard__kpi-card">
                  <p className="admin-dashboard__kpi-label">Total Users</p>
                  <div className="admin-dashboard__kpi-value">
                    <span className="admin-dashboard__kpi-number">{renderKpiValue(stats?.totalUsers)}</span>
                  </div>
                </div>
                <div className="admin-dashboard__kpi-card">
                  <p className="admin-dashboard__kpi-label">Active Users</p>
                  <div className="admin-dashboard__kpi-value">
                    <span className="admin-dashboard__kpi-number">{renderKpiValue(stats?.activeUsers)}</span>
                  </div>
                </div>
                <div className="admin-dashboard__kpi-card">
                  <p className="admin-dashboard__kpi-label">New Signups</p>
                  <div className="admin-dashboard__kpi-value">
                    <span className="admin-dashboard__kpi-number">{renderKpiValue(stats?.newSignups)}</span>
                  </div>
                </div>
                <div className="admin-dashboard__kpi-card">
                  <p className="admin-dashboard__kpi-label">Subscribers</p>
                  <div className="admin-dashboard__kpi-value">
                    <span className="admin-dashboard__kpi-number">{renderKpiValue(stats?.subscribers)}</span>
                  </div>
                </div>
                <div className="admin-dashboard__kpi-card">
                  <p className="admin-dashboard__kpi-label">MRR</p>
                  <div className="admin-dashboard__kpi-value">
                    <span className="admin-dashboard__kpi-number">{renderKpiValue(stats?.mrr, formatMRR)}</span>
                  </div>
                </div>
                <div className="admin-dashboard__kpi-card">
                  <p className="admin-dashboard__kpi-label">Churn Rate</p>
                  <div className="admin-dashboard__kpi-value">
                    <span className="admin-dashboard__kpi-number">{statsLoading ? '—' : (stats?.churnRate ?? 0)}%</span>
                  </div>
                </div>
                <div className="admin-dashboard__kpi-card">
                  <p className="admin-dashboard__kpi-label">Assessments</p>
                  <div className="admin-dashboard__kpi-value">
                    <span className="admin-dashboard__kpi-number">{renderKpiValue(stats?.totalAssessments)}</span>
                  </div>
                </div>
                <div className="admin-dashboard__kpi-card">
                  <p className="admin-dashboard__kpi-label">Consultations</p>
                  <div className="admin-dashboard__kpi-value">
                    <span className="admin-dashboard__kpi-number">{renderKpiValue(stats?.totalConsultations)}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Charts Section */}
            <section className="admin-dashboard__section">
              <div className="admin-dashboard__section-header admin-dashboard__section-header--flexgap">
                <Users className="admin-dashboard__users-icon" />
                <h2 className="admin-dashboard__section-title">User Growth & Trends</h2>
              </div>
              <div className="admin-dashboard__charts">
                <div className="admin-dashboard__chart-card">
                <div className="admin-dashboard__chart-header">
                  <div>
                    <h3 className="admin-dashboard__chart-title">User Growth &amp; Signups</h3>
                    <p className="admin-dashboard__chart-subtitle">Active users vs new signups trend</p>
                  </div>
                  <div className="admin-dashboard__chart-controls">
                    <button className="admin-dashboard__chart-tab admin-dashboard__chart-tab--active">Growth</button>
                    <button className="admin-dashboard__chart-tab">Retention</button>
                  </div>
                </div>
                <div className="admin-dashboard__chart-container">
                  {growthTrendLoading ? (
                    <div className="admin-dashboard__chart-svg-placeholder">Loading…</div>
                  ) : growthTrend.length === 0 ? (
                    <div className="admin-dashboard__chart-svg-placeholder">No data yet</div>
                  ) : (() => {
                    const W = 400;
                    const H = 150;
                    const n = growthTrend.length;
                    const maxVal = Math.max(1, ...growthTrend.map(d => Math.max(d.signups, d.activeUsers)));
                    const pad = 10;
                    const chartH = H - pad * 2;
                    const xs = n <= 1 ? [W / 2] : growthTrend.map((_, i) => (i / (n - 1)) * W);
                    const ySignup = (v: number) => H - pad - (v / maxVal) * chartH;
                    const yActive = (v: number) => H - pad - (v / maxVal) * chartH;
                    const signupPoints = growthTrend.map((d, i) => `${xs[i]},${ySignup(d.signups)}`);
                    const activePoints = growthTrend.map((d, i) => `${xs[i]},${yActive(d.activeUsers)}`);
                    const signupPathD = `M ${signupPoints.join(' L ')}`;
                    const activePathD = `M ${activePoints.join(' L ')}`;
                    const lastX = xs[n - 1];
                    const firstY = ySignup(growthTrend[0].signups);
                    const areaPathD = `${signupPathD} L ${lastX} ${H} L 0 ${H} L 0 ${firstY} Z`;
                    return (
                      <svg className="admin-dashboard__chart-svg" preserveAspectRatio="none" viewBox={`0 0 ${W} ${H}`}>
                        {[0.25, 0.5, 0.75].map((q) => (
                          <line key={q} stroke="currentColor" strokeOpacity={0.15} strokeWidth="1" x1="0" x2={W} y1={H - pad - q * chartH} y2={H - pad - q * chartH} />
                        ))}
                        <path d={areaPathD} fill="url(#adminChartGradientPrimary)" opacity={0.15} />
                        <path d={signupPathD} fill="none" stroke="#2bd4bd" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                        <path d={activePathD} fill="none" stroke="#94a3b8" strokeDasharray="4 4" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                        <defs>
                          <linearGradient id="adminChartGradientPrimary" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor="#2bd4bd" />
                            <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                      </svg>
                    );
                  })()}
                  <div className="admin-dashboard__chart-labels">
                    {growthTrendLoading || growthTrend.length === 0
                      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((l) => <span key={l}>{l}</span>)
                      : growthTrend.map((d) => <span key={d.date}>{d.dayLabel}</span>)}
                  </div>
                </div>
              </div>

              <div className="admin-dashboard__chart-card">
                <div className="admin-dashboard__chart-header">
                  <div>
                    <h3 className="admin-dashboard__chart-title">Revenue &amp; MRR</h3>
                    <p className="admin-dashboard__chart-subtitle">Monthly recurring revenue breakdown</p>
                  </div>
                  <div className="admin-dashboard__chart-legend">
                    <div className="admin-dashboard__chart-legend-dot"></div>
                    <span>₱45,200</span>
                  </div>
                </div>
                <div className="admin-dashboard__chart-container">
                  <div className="admin-dashboard__bar-chart">
                    <div className="admin-dashboard__bar admin-dashboard__bar--h45">
                      <div className="admin-dashboard__bar-fill admin-dashboard__bar-fill--h100"></div>
                      <div className="admin-dashboard__bar-tooltip">₱32k</div>
                    </div>
                    <div className="admin-dashboard__bar admin-dashboard__bar--h55">
                      <div className="admin-dashboard__bar-fill admin-dashboard__bar-fill--h100"></div>
                    </div>
                    <div className="admin-dashboard__bar admin-dashboard__bar--h40">
                      <div className="admin-dashboard__bar-fill admin-dashboard__bar-fill--h100"></div>
                    </div>
                    <div className="admin-dashboard__bar admin-dashboard__bar--h65">
                      <div className="admin-dashboard__bar-fill admin-dashboard__bar-fill--h100"></div>
                    </div>
                    <div className="admin-dashboard__bar admin-dashboard__bar--h70">
                      <div className="admin-dashboard__bar-fill admin-dashboard__bar-fill--h100"></div>
                    </div>
                    <div className="admin-dashboard__bar admin-dashboard__bar--h60">
                      <div className="admin-dashboard__bar-fill admin-dashboard__bar-fill--h100"></div>
                    </div>
                    <div className="admin-dashboard__bar admin-dashboard__bar--highlight admin-dashboard__bar--h85">
                      <div className="admin-dashboard__bar-fill admin-dashboard__bar-fill--h100"></div>
                      <div className="admin-dashboard__bar-tooltip admin-dashboard__bar-tooltip--highlight">₱45k</div>
                    </div>
                  </div>
                  <div className="admin-dashboard__chart-labels">
                    <span>Jan</span>
                    <span>Feb</span>
                    <span>Mar</span>
                    <span>Apr</span>
                    <span>May</span>
                    <span>Jun</span>
                    <span>Jul</span>
                  </div>
                </div>
              </div>
            </div>
            </section>

            {/* Management Section */}
            <section className="admin-dashboard__section">
              <div className="admin-dashboard__section-header admin-dashboard__section-header--flexgap">
                <Settings className="admin-dashboard__settings-icon" />
                <h2 className="admin-dashboard__section-title">Management</h2>
              </div>
              <div className="admin-dashboard__grid">
              {/* Alerts Panel */}
              <div className="admin-dashboard__alerts">
                <div className="admin-dashboard__alerts-header">
                  <h3 className="admin-dashboard__alerts-title">
                    <span className="material-symbols-outlined admin-dashboard__alerts-icon">warning</span>
                    Alerts
                  </h3>
                  {!alertsLoading && alerts.length > 0 && (
                    <span className="admin-dashboard__alerts-badge">{alerts.length} New</span>
                  )}
                </div>
                <div className="admin-dashboard__alerts-list">
                  {alertsLoading ? (
                    <p className="admin-dashboard__alerts-empty">Loading…</p>
                  ) : alerts.length === 0 ? (
                    <p className="admin-dashboard__alerts-empty">No alerts.</p>
                  ) : (
                    alerts.map((alert) => (
                      <div key={alert.id} className={`admin-dashboard__alert admin-dashboard__alert--${alert.type}`}>
                        <div className="admin-dashboard__alert-meta">
                          <span className="admin-dashboard__alert-type">Security</span>
                        </div>
                        <p className="admin-dashboard__alert-message">{alert.message}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Recent Users Table */}
              <div className="admin-dashboard__users">
                <div className="admin-dashboard__users-header">
                  <h3 className="admin-dashboard__users-title">Recent Users</h3>
                </div>
                <div className="admin-dashboard__users-table-container">
                  <table className="admin-dashboard__users-table">
                    <thead className="admin-dashboard__users-table-header">
                      <tr>
                        <th className="admin-dashboard__users-table-cell">User</th>
                        <th className="admin-dashboard__users-table-cell">Status</th>
                        <th className="admin-dashboard__users-table-cell">Plan</th>
                        <th className="admin-dashboard__users-table-cell">Last Active</th>
                        <th className="admin-dashboard__users-table-cell admin-dashboard__users-table-cell--right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="admin-dashboard__users-table-body">
                      {recentUsersLoading ? (
                        <tr className="admin-dashboard__users-table-row">
                          <td colSpan={5} className="admin-dashboard__users-table-cell">Loading…</td>
                        </tr>
                      ) : recentUsers.length === 0 ? (
                        <tr className="admin-dashboard__users-table-row">
                          <td colSpan={5} className="admin-dashboard__users-table-cell">No users yet</td>
                        </tr>
                      ) : (
                        recentUsers.map((row) => {
                          const statusClass = row.status === 'Active' ? 'admin-dashboard__user-status--active' : row.status === 'Free' ? 'admin-dashboard__user-status--offline' : 'admin-dashboard__user-status--pending';
                          return (
                            <tr key={row.id} className="admin-dashboard__users-table-row">
                              <td className="admin-dashboard__users-table-cell">
                                <div className="admin-dashboard__user-info">
                                  <div>
                                    <div className="admin-dashboard__user-name">{row.name}</div>
                                    <div className="admin-dashboard__user-email">{row.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="admin-dashboard__users-table-cell">
                                <span className={`admin-dashboard__user-status ${statusClass}`}>{row.status}</span>
                              </td>
                              <td className="admin-dashboard__users-table-cell">{row.plan}</td>
                              <td className="admin-dashboard__users-table-cell">{formatRelativeTime(row.lastLogin)}</td>
                              <td className="admin-dashboard__users-table-cell admin-dashboard__users-table-cell--right">
                                <button
                                  type="button"
                                  className="admin-dashboard__user-action"
                                  onClick={() => navigate(`/panel/admin/subscriber-management/${row.id}`)}
                                  aria-label="View user"
                                >
                                  <span className="material-symbols-outlined">more_vert</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="admin-dashboard__users-footer">
                  <button type="button" className="admin-dashboard__users-view-all" onClick={() => navigate('/panel/admin/subscriber-management')}>
                    View All Users
                  </button>
                </div>
              </div>

              {/* Recent Payments */}
              <div className="admin-dashboard__payments">
                <div className="admin-dashboard__payments-header">
                  <h3 className="admin-dashboard__payments-title">Recent Payments</h3>
                  <button type="button" className="admin-dashboard__payments-view-all" onClick={() => navigate('/panel/admin/subscriber-management')}>
                    View All
                  </button>
                </div>
                <div className="admin-dashboard__payments-list">
                  <p className="admin-dashboard__payments-empty">No payments yet.</p>
                </div>
              </div>
            </div>
            </section>

            {/* System & Activity Section */}
            <section className="admin-dashboard__section">
              <div className="admin-dashboard__section-header admin-dashboard__section-header--flexgap">
                <Activity className="admin-dashboard__metrics-icon" />
                <h2 className="admin-dashboard__section-title">System & Activity</h2>
              </div>
              <div className="admin-dashboard__bottom">
              <div className="admin-dashboard__system-health">
                <h3 className="admin-dashboard__system-health-title">System Health</h3>
                <div className="admin-dashboard__system-health-grid">
                  <div className="admin-dashboard__system-health-item">
                    <div className={`admin-dashboard__system-health-indicator admin-dashboard__system-health-indicator--${apiStatus === 'online' ? 'online' : apiStatus === 'offline' ? 'offline' : 'checking'}`}>
                      <span className="material-symbols-outlined admin-dashboard__system-health-icon">check_circle</span>
                      {apiStatus === 'online' && <span className="admin-dashboard__system-health-pulse" />}
                    </div>
                    <div>
                      <p className="admin-dashboard__system-health-label">API Status</p>
                      <p className="admin-dashboard__system-health-value">
                        {apiStatus === null ? 'Checking…' : apiStatus === 'online' ? 'Online' : 'Offline'}
                      </p>
                    </div>
                  </div>

                  <div className="admin-dashboard__system-health-item">
                    <span className="material-symbols-outlined admin-dashboard__system-health-icon">speed</span>
                    <div>
                      <p className="admin-dashboard__system-health-label">Latency</p>
                      <p className="admin-dashboard__system-health-value">
                        {apiLatencyMs !== null ? `${apiLatencyMs}ms` : '—'}
                      </p>
                    </div>
                  </div>

                  <div className="admin-dashboard__system-health-item">
                    <span className="material-symbols-outlined admin-dashboard__system-health-icon">bug_report</span>
                    <div>
                      <p className="admin-dashboard__system-health-label">Error Rate</p>
                      <p className="admin-dashboard__system-health-value">N/A</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="admin-dashboard__activity">
                <h3 className="admin-dashboard__activity-title">Admin Activity</h3>
                <div className="admin-dashboard__activity-list">
                  <p className="admin-dashboard__activity-empty">No recent activity.</p>
                </div>
                <button type="button" className="admin-dashboard__activity-view-all" disabled>
                  View All
                </button>
              </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
